import React, { useEffect, useMemo } from 'react';
import { Alert } from '@mui/material';
import styled from 'styled-components';
import { useAppThunkDispatch, useTypedSelector } from '@Features/store';
import { GridLayout } from '@Styled/utilities';
import BunnyCard from './Components/BunnyCard';
import VdocipherCard from './Components/VdocipherCard';
import GoogleDriveCard from './Components/GoogleDriveCard';
import ContainersTable from './Components/ContainersTable';
import UsageTable from './Components/UsageTable';
import { videoProvidersFetch } from './Slices/VideoProviderSettings.slice';
import { VideoProviderName } from './Types/VideoProviders.types';

/**
 * Settings -> Video Providers. Isolated from app_settings by design (that row
 * is publicly served): credentials live encrypted in video_provider_settings
 * and are write-only through these cards.
 */
const VideoProvidersTab = () => {
	const dispatch = useAppThunkDispatch();
	const { providers, errors } = useTypedSelector(
		(state) => state.VideoProviderSettings
	);

	useEffect(() => {
		dispatch(videoProvidersFetch());
	}, [dispatch]);

	const byName = useMemo(
		() => new Map(providers.map((provider) => [provider.provider, provider])),
		[providers]
	) as Map<VideoProviderName, typeof providers[number]>;

	const encryptionReady =
		providers.length === 0 || providers.every((row) => row.encryption_ready);

	const bunny = byName.get('bunny');
	const vdocipher = byName.get('vdocipher');
	const googleDrive = byName.get('google_drive');

	return (
		<Container>
			{errors.fetch && <Alert severity="error">{errors.fetch}</Alert>}
			{!encryptionReady && (
				<Alert severity="warning">
					VIDEO_PROVIDERS_ENCRYPTION_KEY is not set on this server — provider
					secrets cannot be stored until it is configured.
				</Alert>
			)}
			{providers.length > 0 && (
				<CardsLayout>
					{/* Two explicit columns: the tall Bunny card owns the left one;
					    the three short cards stack on the right so they run alongside
					    it instead of dropping below a grid row sized to Bunny. */}
					<CardsColumn>{bunny && <BunnyCard setting={bunny} />}</CardsColumn>
					<CardsColumn>
						{vdocipher && <VdocipherCard setting={vdocipher} />}
						{googleDrive && <GoogleDriveCard setting={googleDrive} />}
					</CardsColumn>
				</CardsLayout>
			)}
			<ContainersTable />
			<UsageTable />
		</Container>
	);
};

const Container = styled.div`
	padding: 2rem;
	display: flex;
	flex-direction: column;
	gap: 2rem;
`;

const CardsLayout = styled(GridLayout)`
	grid-template-columns: 1fr 1fr;
	gap: 1.5rem;
	align-items: start;

	@media (max-width: 1200px) {
		grid-template-columns: 1fr;
	}
`;

const CardsColumn = styled.div`
	display: flex;
	flex-direction: column;
	gap: 1.5rem;
`;

export default VideoProvidersTab;
